import {
  convertAccountingSheet,
  convertDart,
  convertDsdOrExcel,
  type DartConvertFormat,
} from "@/apis/dsd/route";
import { ApiError, isDsdSectionNotFoundError } from "@/apis/errors";
import {
  DSD_SECTION_NOT_FOUND_DART_URL_MESSAGE,
  DSD_SECTION_NOT_FOUND_MESSAGE,
} from "@/constants/dsd-upload";
import { downloadBlob } from "@/utils/download-blob";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";

// DRM/구버전 xls 처럼 사용자가 직접 고칠 수 있는 업로드는 백엔드 안내 문구를 그대로 띄운다.
// 화이트리스트인 이유는 다른 400 응답의 message 가 영어 기본값("Bad request")이기 때문.
const UNOPENABLE_FILE_ERROR_CODES = [
  "DRM_PROTECTED_FILE_ERROR",
  "UNSUPPORTED_EXCEL_FORMAT_ERROR",
];

/**
 * 세 변환 공통 키. 변환이 도는 동안 화면 이동을 막는 쪽이 `useIsMutating` 으로 본다.
 *
 * 여러 화면이 같은 훅을 쓰므로 이 키만 보면 다른 화면에서 시작한 변환까지 잡힌다.
 * 자기 화면 것만 보려면 `[...DSD_CONVERT_MUTATION_KEY, "<scope>"]` 를 훅에 넘길 것
 * (`useIsMutating` 의 mutationKey 는 prefix 매칭이라 공통 키 구독은 그대로 동작한다).
 */
export const DSD_CONVERT_MUTATION_KEY = ["dsd-convert"];

type ConvertMutationKey = readonly unknown[];

function outputFilename(input: string): string {
  const dot = input.lastIndexOf(".");
  const stem = dot > 0 ? input.slice(0, dot) : input;
  const ext = dot > 0 ? input.slice(dot).toLowerCase() : "";
  return ext === ".dsd" ? `${stem}.xlsx` : `${stem}.dsd`;
}

/** DSD ↔ 엑셀 파일 변환. 성공하면 결과 파일을 내려받는다. */
export const useConvertDsdOrExcel = (
  mutationKey: ConvertMutationKey = DSD_CONVERT_MUTATION_KEY,
) =>
  useMutation({
    mutationKey,
    mutationFn: (file: File) => convertDsdOrExcel(file),
    onSuccess: (blob, file) => downloadBlob(blob, outputFilename(file.name)),
    onError: (error) => {
      if (isDsdSectionNotFoundError(error)) {
        toast.error(DSD_SECTION_NOT_FOUND_MESSAGE);
      } else if (
        error instanceof ApiError &&
        UNOPENABLE_FILE_ERROR_CODES.includes(error.errorCode)
      ) {
        // message 는 응답에 없을 수 있다(ApiError 는 undefined 를 받으면 빈 문자열이 된다).
        // 빈 토스트는 "아무 안내 없이 실패"로 읽히므로 최소 안내를 대신 띄운다.
        toast.error(
          error.message.trim() ||
            "파일을 열 수 없습니다. 보안(DRM) 해제 후 다시 업로드해주세요.",
        );
      } else {
        toast.error("파일을 변환할 수 없습니다. 올바른 파일인지 확인해주세요.");
      }
    },
  });

/** 검토·감사보고서가 아닌 URL(잘못된 URL·보고서 본문) 은 사용자에게 같은 행동을 요구하므로 문구도 하나다. */
const NOT_REVIEW_OR_AUDIT_REPORT_MESSAGE =
  "검토·감사보고서만 불러올 수 있습니다. 검토·감사보고서 선택 후 URL을 붙여넣어 주세요.";

const DART_CONVERT_FAILED_MESSAGE = "DART URL 변환에 실패하였습니다.";

/**
 * DART URL 변환 실패의 분류는 이 함수 한 곳에만 둔다.
 * 백엔드 에러 코드가 늘면 여기만 고치면 되고, 화면마다 다른 것은 문구 매핑뿐이다.
 */
type DartConvertErrorKind = "section-not-found" | "wrong-url" | "unknown";

const classifyDartConvertError = (error: unknown): DartConvertErrorKind => {
  if (isDsdSectionNotFoundError(error)) return "section-not-found";
  if (error instanceof ApiError && error.errorCode === "WRONG_DART_URL_ERROR")
    return "wrong-url";
  return "unknown";
};

/** DART URL 변환 실패를 입력 필드 하단에 그대로 쓸 수 있는 한 줄 문구로 바꾼다. */
export const dartConvertErrorMessage = (error: unknown): string =>
  classifyDartConvertError(error) === "unknown"
    ? DART_CONVERT_FAILED_MESSAGE
    : NOT_REVIEW_OR_AUDIT_REPORT_MESSAGE;

/** 토스트로 보여주는 화면(team-workspace)의 기존 문구. 분류는 위와 공유한다. */
const dartConvertErrorToast = (error: unknown) => {
  switch (classifyDartConvertError(error)) {
    case "section-not-found":
      return DSD_SECTION_NOT_FOUND_DART_URL_MESSAGE;
    case "wrong-url":
      return (
        <span>
          잘못된 URL입니다. <br />
          검토/감사보고서를 선택한 후 URL을 복사해주세요.
        </span>
      );
    default:
      return DART_CONVERT_FAILED_MESSAGE;
  }
};

/** DART 공시 URL 의 감사/검토보고서를 DSD 또는 엑셀로 변환해 내려받는다. */
export const useConvertDart = (
  mutationKey: ConvertMutationKey = DSD_CONVERT_MUTATION_KEY,
  /** 에러를 입력 필드 하단 등 호출부에서 직접 보여주는 화면이 토스트를 끈다. */
  { silent = false }: { silent?: boolean } = {},
) =>
  useMutation({
    mutationKey,
    mutationFn: ({
      url,
      toFormat,
    }: {
      url: string;
      toFormat: DartConvertFormat;
    }) => convertDart(url, toFormat),
    onSuccess: ({ blob, filename }) => downloadBlob(blob, filename),
    onError: silent
      ? undefined
      : (error) => toast.error(dartConvertErrorToast(error)),
  });

/** 정산표(엑셀)를 보고서 종류에 맞는 DSD 로 변환해 내려받는다. */
export const useConvertAccountingSheet = (
  mutationKey: ConvertMutationKey = DSD_CONVERT_MUTATION_KEY,
) =>
  useMutation({
    mutationKey,
    mutationFn: ({
      file,
      params,
    }: {
      file: File;
      params: Parameters<typeof convertAccountingSheet>[1];
    }) => convertAccountingSheet(file, params),
    onSuccess: ({ blob, filename }) => downloadBlob(blob, filename),
    onError: (error) => {
      toast.error(
        (error instanceof ApiError && error.message) ||
          "변환에 실패하였습니다. 파일을 확인해주세요.",
      );
    },
  });
